/**
 * <TutorLiveControlBar> — la barre du bas du live du masteur (molecule).
 *
 * Les gestes de l'ancien pied (`LiveModeFooter` et sa `ControlBarM`), dans l'ordre de la maquette
 * L3 : le micro et la caméra (avec, sur le web, le chevron de l'appareil), partager l'écran (il passe à
 * l'univers pendant le partage), le chat et ses non-lus (le fil « Tous » et ceux des élèves), « Plus »
 * (les gestes de groupe et « Un problème ? », `onOpenMore`), et la fin du live. La mosaïque y revient au
 * round 3 (`mosaic`, l'ancien `LayoutToggleM`) : tout le groupe sur tout l'écran, ou l'élève regardé —
 * la bascule de l'onglet Visio du round 2 est retirée.
 *
 * Les boutons sont les poussoirs carrés du livre, leur glyphe seul, comme la barre du centre (05/11, `FACES`) ; les
 * mots du micro et de la caméra sont ceux de la barre de l'élève (`<LiveControlBar>`). Coupé, le glyphe barré dans
 * le rouge ; partagé, le principal de l'univers (et l'écran barré, qui l'arrête) ; la fin dans le rouge de ce qui
 * détruit. Un micro ou une caméra en défaut portent le point
 * (l'ancien « ! »). « Plus » n'en porte plus (05/11) : chaque problème qu'il comptait se dit déjà là où il
 * est — le micro muet ou refusé sur le micro, la caméra refusée sur la caméra, ma connexion faible dans les
 * alertes du centre.
 *
 * **Le bandeau du haut est retiré au round 4** (Wajih : « La barre d'en haut, il faut qu'on s'en
 * débarrasse ») : ce qu'il portait descend ici. À gauche, le masteur (sa capsule, « Camille · 3 élèves »)
 * et le direct, sa pastille qui bat et sa durée (`presence`) ; on « accepte de les perdre » quand la barre
 * est étroite (sous 1 024 px de barre : la tablette en portrait, le téléphone). À droite, la cloche de « À
 * traiter » quand la colonne est en tiroir (`bell`), elle, toujours là.
 *
 * **« + Activité »** (`onAddActivity`, Wajih, live du 04/11 : « on ne trouvait pas où générer ») : le geste le plus
 * fréquent du live, en tête des boutons, au téléphone seulement. Ailleurs il est dans la barre flottante du centre
 * (`<TutorLiveActionBar>`, 04/11 au soir) : la page ne le donne ici qu'au téléphone, qui n'en a pas.
 *
 * Elle ne dessine pas de surface : la page la pose. Sous 420 px de barre, les boutons passent à la
 * cible minimale et se serrent : la fin reste à l'écran.
 */
import React from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { formatCountdown } from "../../../ports/primary/ui/formatters/dateFormatter"
import { countLabel } from "../../../ports/primary/ui/formatters/textFormatter"
import { tutorPendingLabel } from "../../../ports/primary/ui/formatters/tutorSessionFormatter"
import { type UILiveParticipant } from "../../../ports/primary/ui/types/studentLive"
import { Avatar } from "../../atoms/Avatar"
import { Button } from "../../atoms/Buttons/Button"
import { actionFace, type ButtonSize, PushButton } from "../../atoms/Buttons/Button/Button"
import { IconButton } from "../../atoms/Buttons/IconButton"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { LiveDot } from "../../atoms/LiveDot"
import { Text } from "../../atoms/typography/Text"
import { useSecondClock } from "../../hooks/useSecondClock"
import { useWidthAtLeast } from "../../hooks/useWidthAtLeast"
import { BadgedIconButton } from "../BadgedIconButton"
import { LIVE_DEVICE_LABELS } from "../LiveControlBar/LiveControlBar"

export type TutorLiveControlBarProps = {
    micOn: boolean
    onToggleMic: () => void
    cameraOn: boolean
    onToggleCamera: () => void
    micBlocked?: boolean
    cameraBlocked?: boolean
    onOpenMicDevices?: () => void
    onOpenCameraDevices?: () => void
    /** Le partage d'écran. Absent (un navigateur qui ne sait pas, un téléphone) : pas de bouton. */
    screenShare?: { on: boolean; pending?: boolean; onToggle: () => void }
    /** La mosaïque : tout le groupe sur tout l'écran (`on`), ou la salle. Absente : pas de bouton. */
    mosaic?: { on: boolean; onToggle: () => void }
    /** Les messages non lus, tous fils confondus. Défaut 0. */
    unreadCount?: number
    onOpenChat: () => void
    onOpenMore: () => void
    onEnd: () => void
    /** La sortie part : la fin tourne. Défaut `false`. */
    ending?: boolean
    /** Le masteur et le direct, à gauche (l'ancien bandeau) : montrés à partir de 1 024 px de barre. */
    presence?: { tutor: UILiveParticipant; studentCount: number; startedAt: number }
    /** La cloche de « À traiter » en tiroir, à droite : son compte, l'accent quand un signal d'élève attend. */
    bell?: { count: number; urgent?: boolean; open?: boolean; onPress: () => void }
    /** « + Activité » : générer une activité, toujours à portée. Absent : pas de bouton. */
    onAddActivity?: () => void
    testID?: string
}

const TEXTS = {
    ...LIVE_DEVICE_LABELS,
    micDevices: "Choisir mon micro",
    cameraDevices: "Choisir ma caméra",
    share: "Partager l'écran",
    mosaic: "Tout le groupe en mosaïque",
    room: "Revenir à l'élève regardé",
    stopShare: "Arrêter le partage",
    chat: "Chat",
    unread: (count: number) => countLabel(count, "message non lu", "messages non lus"),
    more: "Plus",
    end: "Fin du live",
    who: (name: string, count: number) => `${name.split(" ")[0] ?? name} · ${countLabel(count, "élève")}`,
    live: "LIVE",
    since: (elapsed: string) => `En direct depuis ${elapsed}`,
    bell: "À traiter",
    add: "Activité",
    addLabel: "Ajouter une activité",
}

/**
 * La largeur de barre où le masteur et le direct tiennent à gauche sans pousser les boutons hors du centre :
 * une colonne de chaque côté d'eux (~ 260 px) et les neuf boutons. Une tablette en paysage (1 024 px, une
 * barre de défilement comprise) la garde.
 */
const PRESENCE_FROM = 1000
const TUTOR_SIZE = 32

/** Le direct et sa durée : seul à suivre la seconde, pour que la barre ne se redessine pas avec lui. */
function LiveClock(props: { startedAt: number }) {
    const now = useSecondClock(props.startedAt)
    const elapsed = formatCountdown(Math.max(0, (now - props.startedAt) / 1000))
    return (
        <View accessible accessibilityLabel={TEXTS.since(elapsed)} style={styles.live}>
            <LiveDot />
            <Text size="text-xs" weight="extrabold" color="danger-strong">
                {TEXTS.live}
            </Text>
            <Text size="text-sm" weight="bold" color="text-strong">
                {elapsed}
            </Text>
        </View>
    )
}

/**
 * Les boutons sont les poussoirs carrés du livre, leur glyphe seul (Wajih, 05/11 : « des boutons-icônes du DS »),
 * comme la barre du centre : au repos le secondaire ; un micro ou une caméra coupés, le secondaire écrit dans le
 * rouge (le glyphe barré) ; allumé (le partage, la mosaïque, la cloche ouverte), le principal de l'univers ; la
 * cloche urgente, l'orange d'alerte (le jaune reste interdit à un bouton) ; la fin, le principal de ce qui détruit.
 */
const FACES = {
    rest: actionFace("outline", "role-fill"),
    muted: actionFace("outline", "danger"),
    chosen: actionFace("solid", "role-fill"),
    urgent: actionFace("solid", "warning"),
    end: actionFace("solid", "danger"),
}
const TIGHT_BELOW = 420
/** Un bouton du live allumé (le côte à côte du centre) : l'aplat choisi de l'univers. */
export const LIVE_CHOSEN_FACE = { color: "role-selected", iconColor: "text-on-role" } as const

export function TutorLiveControlBar(props: TutorLiveControlBarProps) {
    const {
        micOn,
        onToggleMic,
        cameraOn,
        onToggleCamera,
        micBlocked = false,
        cameraBlocked = false,
        onOpenMicDevices,
        onOpenCameraDevices,
        screenShare,
        mosaic,
        unreadCount = 0,
        onOpenChat,
        onOpenMore,
        onEnd,
        ending = false,
        presence,
        bell,
        onAddActivity,
        testID,
    } = props
    const { onLayout: onLayoutTight, atLeast: fits } = useWidthAtLeast(TIGHT_BELOW)
    const { onLayout: onLayoutWide, atLeast: wide } = useWidthAtLeast(PRESENCE_FROM)
    const onLayout = (event: Parameters<typeof onLayoutTight>[0]) => {
        onLayoutTight(event)
        onLayoutWide(event)
    }
    const size: ButtonSize = fits ? "md" : "sm"

    const chevron = (onOpen: (() => void) | undefined, label: string) =>
        onOpen != null && fits ? (
            <IconButton
                iconName="chevron-up"
                appearance="transparent"
                iconColor="primary"
                size={26}
                iconSize={14}
                onPress={onOpen}
                accessibilityLabel={label}
            />
        ) : null

    return (
        <View onLayout={onLayout} testID={testID}>
            <Row align="center" justify="center" style={styles.row}>
                {wide && presence != null && (
                    <Row gap="3" align="center" style={styles.side}>
                        <Avatar
                            uri={presence.tutor.avatarUrl}
                            name={presence.tutor.name}
                            size={TUTOR_SIZE}
                            variant="tutor"
                        />
                        <Stack gap="0" style={styles.shrink}>
                            <Text size="text-sm" weight="bold" color="text-strong" numberOfLines={1}>
                                {TEXTS.who(presence.tutor.name, presence.studentCount)}
                            </Text>
                            <LiveClock startedAt={presence.startedAt} />
                        </Stack>
                    </Row>
                )}
                <Row gap={fits ? "3" : "1"} align="center" justify="center" style={styles.controls}>
                    {onAddActivity != null &&
                        (fits ? (
                            <Button
                                label={TEXTS.add}
                                accessibilityLabel={TEXTS.addLabel}
                                iconName="plus"
                                size="sm"
                                onPress={onAddActivity}
                            />
                        ) : (
                            <PushButton
                                iconOnly
                                label={TEXTS.addLabel}
                                iconName="plus"
                                face={FACES.chosen}
                                size={size}
                                onPress={onAddActivity}
                            />
                        ))}
                    <Row gap="0" align="center">
                        <BadgedIconButton
                            iconName={micOn ? "microphone" : "microphone-slash"}
                            push={{ face: micOn ? FACES.rest : FACES.muted, size }}
                            dot={micBlocked}
                            dotLabel={TEXTS.blocked}
                            onPress={onToggleMic}
                            accessibilityLabel={micOn ? TEXTS.micOn : TEXTS.micOff}
                        />
                        {chevron(onOpenMicDevices, TEXTS.micDevices)}
                    </Row>
                    <Row gap="0" align="center">
                        <BadgedIconButton
                            iconName={cameraOn ? "video" : "video-slash"}
                            push={{ face: cameraOn ? FACES.rest : FACES.muted, size }}
                            dot={cameraBlocked}
                            dotLabel={TEXTS.blocked}
                            onPress={onToggleCamera}
                            accessibilityLabel={cameraOn ? TEXTS.cameraOn : TEXTS.cameraOff}
                        />
                        {chevron(onOpenCameraDevices, TEXTS.cameraDevices)}
                    </Row>
                    {screenShare != null && (
                        <PushButton
                            iconOnly
                            // Pendant le partage, le bouton dit ce qu'il fait : l'arrêter (l'écran barré).
                            label={screenShare.on ? TEXTS.stopShare : TEXTS.share}
                            iconName={screenShare.on ? "desktop-slash" : "desktop"}
                            face={screenShare.on ? FACES.chosen : FACES.rest}
                            size={size}
                            loading={screenShare.pending === true}
                            onPress={screenShare.onToggle}
                        />
                    )}
                    {mosaic != null && (
                        <PushButton
                            iconOnly
                            label={mosaic.on ? TEXTS.room : TEXTS.mosaic}
                            iconName="th-large"
                            face={mosaic.on ? FACES.chosen : FACES.rest}
                            size={size}
                            onPress={mosaic.onToggle}
                        />
                    )}
                    <BadgedIconButton
                        iconName="comment-dots"
                        push={{ face: FACES.rest, size }}
                        count={unreadCount}
                        countLabel={TEXTS.unread}
                        onPress={onOpenChat}
                        accessibilityLabel={TEXTS.chat}
                    />
                    <PushButton
                        iconOnly
                        label={TEXTS.more}
                        iconName="ellipsis-h"
                        face={FACES.rest}
                        size={size}
                        onPress={onOpenMore}
                    />
                    <PushButton
                        iconOnly
                        label={TEXTS.end}
                        iconName="phone-slash"
                        face={FACES.end}
                        size={size}
                        loading={ending}
                        onPress={onEnd}
                    />
                    {bell != null && (
                        <BadgedIconButton
                            iconName="bell"
                            push={{ face: bell.open ? FACES.chosen : bell.urgent ? FACES.urgent : FACES.rest, size }}
                            count={bell.count}
                            countLabel={tutorPendingLabel}
                            onPress={bell.onPress}
                            accessibilityLabel={TEXTS.bell}
                        />
                    )}
                </Row>
                {wide && presence != null && <View style={styles.side} />}
            </Row>
        </View>
    )
}

const styles = StyleSheet.create((theme) => ({
    /** Les comptes débordent en haut de leur bouton : la rangée leur laisse la place. */
    row: { paddingTop: theme.spacing["2"], paddingHorizontal: theme.spacing["2"] },
    controls: { flexShrink: 0 },
    /** Les deux côtés se partagent ce qui reste : les boutons restent au centre de l'écran. */
    side: { flex: 1, minWidth: 0 },
    shrink: { flexShrink: 1, minWidth: 0 },
    live: { flexDirection: "row", alignItems: "center", gap: theme.spacing["1"] },
}))
